import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Menu,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  MenuBook as CurricularIcon,
  EmojiEvents as ExtracurricularIcon,
  SwapHoriz as TransferIcon,
  Print as PrintIcon,
  TableChart as ExcelIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, 
  Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { TABLES, getCurrentSchoolYear, getSchoolYears, resolveSchoolYear } from '../lib/tables';
import SchoolYearSelect from '../components/SchoolYearSelect';

export default function Overview() {
  const location = useLocation();
  const { profile } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  
  const [selectedClass, setSelectedClass] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table'); 
  const [selectedChart, setSelectedChart] = useState('creditsByClass'); 
  
  const [stats, setStats] = useState({
    totalStudents: 0,
    averageCredits: 0,
    pendingRequests: 0,
    completedModules: 0,
  });
  
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  
  
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportType, setExportType] = useState('all'); 
  const [exportClassFilter, setExportClassFilter] = useState('all');
  const [exportSpecFilter, setExportSpecFilter] = useState('all');

  const [selectedYear, setSelectedYear] = useState(null);
  const [yearOptions, setYearOptions] = useState([]);

  
  const CHART_COLORS = ['#2196F3', '#4CAF50', '#FF9800', '#E91E63', '#9C27B0', '#00BCD4'];
  const CATEGORY_COLORS = {
    curricular: '#1976D2',
    extracurricular: '#F57C00', 
    transferabil: '#7B1FA2'
  };

  
  const SPECIALIZATIONS = [
    { code: 'MI', name: 'Matematică-Informatică' },
    { code: 'SN', name: 'Științe ale Naturii' },
    { code: 'SS', name: 'Științe Sociale' },
    { code: 'FL', name: 'Filologie' },
    { code: 'BU', name: 'Business' },
  ];

  
  const chartOptions = [
    { value: 'creditsByClass', label: 'Credite pe Clase', icon: <BarChartIcon /> },
    { value: 'categoryDistribution', label: 'Distribuție Categorii', icon: <PieChartIcon /> },
    { value: 'topStudents', label: 'Top 10 Elevi', icon: <TrendingUpIcon /> },
    { value: 'creditDistribution', label: 'Distribuție Credite', icon: <PeopleIcon /> },
    { value: 'stackedCategories', label: 'Categorii per Clasă', icon: <SchoolIcon /> },
  ];

  useEffect(() => {
    let mounted = true;
    getSchoolYears(TABLES.credit_requests).then((years) => {
      if (!mounted) return;
      setYearOptions(years);
      setSelectedYear((prev) => prev ?? resolveSchoolYear(years));
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let isMounted = true;
    
    const loadAsync = async () => {
      if (profile && profile.rol === 'coordonator' && isMounted && selectedYear) {
        await loadOverviewData(isMounted);
      }
    };
    
    loadAsync();
    return () => { isMounted = false; };
  }, [profile, location.pathname, selectedYear]);

  const getSpecializationName = (code) => {
    const specializationsMap = {
      'MI': 'Matematică-Informatică',
      'SN': 'Științe ale Naturii',
      'SS': 'Științe Sociale',
      'FL': 'Filologie',
      'BU': 'Business',
    };
    return specializationsMap[code] || code || 'N/A';
  };

  const loadOverviewData = async (isMounted = true) => {
    setLoading(true);
    try {
      const { data: studentsData, error: studentsError } = await supabase
        .from(TABLES.profiles)
        .select('*')
        .eq('rol', 'elev')
        .order('clasa')
        .order('nume');
      
      if (!isMounted) return;

      if (studentsError) throw studentsError;

      const { data: creditTypes } = await supabase
        .from(TABLES.credit_types)
        .select('id, nume_tip, categorie');

      const creditTypesMap = {};
      creditTypes?.forEach(ct => {
        creditTypesMap[ct.id] = ct.categorie || ct.nume_tip?.toLowerCase() || '';
      });
      
      const yearFilter = selectedYear === 'all' ? yearOptions : [selectedYear || getCurrentSchoolYear()];

      const { data: modulesData } = await supabase
        .from(TABLES.modules)
        .select('*')
        .order('ordine');

      const studentsWithCredits = await Promise.all(
        (studentsData || []).map(async (student) => {
          const { data: requestsData } = await supabase
            .from(TABLES.credit_requests)
            .select('status, id_tip_credit, id_module, credite_solicitate, credite_acordate')
            .eq('id_elev', student.id)
            .in('an_scolar', yearFilter);

          const pendingRequests = requestsData?.filter(r => r.status === 'in_asteptare').length || 0;
          const validatedRequests = requestsData?.filter(r => r.status === 'validat').length || 0;
          const rejectedRequests = requestsData?.filter(r => r.status === 'respins').length || 0;

          const validatedWithType = requestsData?.filter(r => r.status === 'validat') || [];
          let crediteCurriculare = 0;
          let crediteExtracurriculare = 0;
          let crediteTransferabile = 0;

          const moduleProgressMap = {};
          
          validatedWithType.forEach(req => {
            const categorie = (creditTypesMap[req.id_tip_credit] || '').toLowerCase();
            const credite = req.credite_acordate || req.credite_solicitate || 0;
            
            if (validatedWithType.length > 0 && student.id === studentsData[0]?.id) {
            }
            
            if (categorie.includes('curricular') && !categorie.includes('extra')) {
              crediteCurriculare += credite;
            } else if (categorie.includes('extracurricular') || categorie.includes('extra')) {
              crediteExtracurriculare += credite;
            } else if (categorie.includes('transfer')) {
              crediteTransferabile += credite;
            } else {
            }
            
            if (req.id_module) {
              const mod = modulesData?.find(m => m.id === req.id_module);
              const moduleCod = mod?.cod_module || req.id_module;
              if (!moduleProgressMap[moduleCod]) {
                moduleProgressMap[moduleCod] = { module: moduleCod, credits: 0 };
              }
              moduleProgressMap[moduleCod].credits += credite;
            }
          });

          const totalCredits = crediteCurriculare + crediteExtracurriculare + crediteTransferabile;
          
          const modulesProgress = Object.values(moduleProgressMap);

          return {
            ...student,
            totalCredits,
            pendingRequests,
            validatedRequests,
            rejectedRequests,
            crediteCurriculare,
            crediteExtracurriculare,
            crediteTransferabile,
            modulesProgress,
            completedModules: modulesProgress.length,
          };
        })
      );

      setStudents(studentsWithCredits);

      const uniqueClasses = [...new Set(studentsWithCredits.map(s => s.clasa).filter(Boolean))];
      setClasses(uniqueClasses);

      const totalStudents = studentsWithCredits.length;
      const totalCredits = studentsWithCredits.reduce((sum, s) => sum + s.totalCredits, 0);
      const totalPending = studentsWithCredits.reduce((sum, s) => sum + s.pendingRequests, 0);
      const totalCompleted = studentsWithCredits.reduce((sum, s) => sum + s.completedModules, 0);

      setStats({
        totalStudents,
        averageCredits: totalStudents > 0 ? Math.round(totalCredits / totalStudents * 10) / 10 : 0,
        pendingRequests: totalPending,
        completedModules: totalCompleted,
      });

    } catch (error) {
      console.error('Error loading overview data:', error);
    } finally {
      if (isMounted) setLoading(false);
    }
  };

  const filteredStudents = useMemo(() => {
    let filtered = students;

    if (selectedClass !== 'all') {
      filtered = filtered.filter(student => student.clasa === selectedClass);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(student =>
        student.nume.toLowerCase().includes(term) ||
        student.prenume.toLowerCase().includes(term) ||
        student.email.toLowerCase().includes(term) ||
        student.clasa?.toLowerCase().includes(term) ||
        student.specializare?.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [students, selectedClass, searchTerm]);

  
  const chartData = useMemo(() => {
    
    const creditsByClass = classes.map(clasa => {
      const classStudents = filteredStudents.filter(s => s.clasa === clasa);
      const totalCredits = classStudents.reduce((sum, s) => sum + (s.totalCredits || 0), 0);
      const avgCredits = classStudents.length > 0 ? Math.round(totalCredits / classStudents.length * 10) / 10 : 0;
      return {
        name: clasa,
        elevi: classStudents.length,
        credite: totalCredits,
        medie: avgCredits,
        curricular: classStudents.reduce((sum, s) => sum + (s.creditsByCategory?.curricular || 0), 0),
        extracurricular: classStudents.reduce((sum, s) => sum + (s.creditsByCategory?.extracurricular || 0), 0),
        transferabil: classStudents.reduce((sum, s) => sum + (s.creditsByCategory?.transferabil || 0), 0),
      };
    }).filter(c => c.elevi > 0);

    
    const totalCurricular = filteredStudents.reduce((sum, s) => sum + (s.creditsByCategory?.curricular || 0), 0);
    const totalExtracurricular = filteredStudents.reduce((sum, s) => sum + (s.creditsByCategory?.extracurricular || 0), 0);
    const totalTransferabil = filteredStudents.reduce((sum, s) => sum + (s.creditsByCategory?.transferabil || 0), 0);
    
    const categoryDistribution = [
      { name: 'Curriculare', value: totalCurricular, color: CATEGORY_COLORS.curricular },
      { name: 'Extracurriculare', value: totalExtracurricular, color: CATEGORY_COLORS.extracurricular },
      { name: 'Transferabile', value: totalTransferabil, color: CATEGORY_COLORS.transferabil },
    ].filter(c => c.value > 0);

    
    const topStudents = [...filteredStudents]
      .sort((a, b) => (b.totalCredits || 0) - (a.totalCredits || 0))
      .slice(0, 10)
      .map(s => ({
        name: `${s.prenume} ${s.nume.charAt(0)}.`,
        credite: s.totalCredits || 0,
        clasa: s.clasa,
      }));

    
    const creditRanges = [
      { range: '0', min: 0, max: 0 },
      { range: '1-50', min: 1, max: 50 },
      { range: '51-100', min: 51, max: 100 },
      { range: '101-150', min: 101, max: 150 },
      { range: '151-200', min: 151, max: 200 },
      { range: '200+', min: 201, max: Infinity },
    ];
    const creditDistribution = creditRanges.map(r => ({
      name: r.range,
      elevi: filteredStudents.filter(s => {
        const credits = s.totalCredits || 0;
        return credits >= r.min && credits <= r.max;
      }).length,
    }));

    return {
      creditsByClass,
      categoryDistribution,
      topStudents,
      creditDistribution,
    };
  }, [filteredStudents, classes, CATEGORY_COLORS]);

  
  const handleExportCSV = () => {
    setExportMenuAnchor(null);
    setExportType('all');
    setExportClassFilter('all');
    setExportSpecFilter('all');
    setExportDialogOpen(true);
  };

  
  const performExport = () => {
    
    let studentsToExport = [...filteredStudents];
    let exportFileName = 'centralizare_credite';
    
    if (exportType === 'byClass' && exportClassFilter !== 'all') {
      studentsToExport = studentsToExport.filter(s => s.clasa === exportClassFilter);
      exportFileName = `centralizare_clasa_${exportClassFilter}`;
    } else if (exportType === 'bySpecialization' && exportSpecFilter !== 'all') {
      studentsToExport = studentsToExport.filter(s => s.specializare === exportSpecFilter);
      const specName = SPECIALIZATIONS.find(sp => sp.code === exportSpecFilter)?.name || exportSpecFilter;
      exportFileName = `centralizare_spec_${exportSpecFilter}`;
    }

    if (studentsToExport.length === 0) {
      alert('Nu există elevi care să corespundă filtrelor selectate.');
      return;
    }

    
    const headers = [
      'Nume', 'Prenume', 'Clasa', 'Specializare', 'Email', 'Telefon',
      'Credite Totale', 'Credite Curriculare', 'Credite Extracurriculare', 'Credite Transferabile',
      'Cereri In Asteptare', 'Cereri Validate', 'Cereri Respinse', 'Module Active', 'Data Inregistrare'
    ];
    
    
    let tableHTML = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
      <head>
        <meta charset="utf-8">
        <!--[if gte mso 9]><xml>
          <x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
            <x:Name>Centralizare</x:Name>
            <x:WorksheetOptions><x:Panes></x:Panes></x:WorksheetOptions>
          </x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook>
        </xml><![endif]-->
        <style>td, th { border: 1px solid #ccc; padding: 5px; } th { background: #f0f0f0; font-weight: bold; }</style>
      </head>
      <body>
        <table border="1">
          <thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
          <tbody>
    `;
    
    studentsToExport.forEach(student => {
      tableHTML += `<tr>
        <td>${student.nume || ''}</td>
        <td>${student.prenume || ''}</td>
        <td>${student.clasa || ''}</td>
        <td>${getSpecializationName(student.specializare)}</td>
        <td>${student.email || ''}</td>
        <td>${student.telefon || ''}</td>
        <td>${student.totalCredits || 0}</td>
        <td>${student.crediteCurriculare || 0}</td>
        <td>${student.crediteExtracurriculare || 0}</td>
        <td>${student.crediteTransferabile || 0}</td>
        <td>${student.pendingRequests || 0}</td>
        <td>${student.validatedRequests || 0}</td>
        <td>${student.rejectedRequests || 0}</td>
        <td>${student.completedModules || 0}</td>
        <td>${student.created_at ? new Date(student.created_at).toLocaleDateString('ro-RO') : ''}</td>
      </tr>`;
    });
    
    tableHTML += '</tbody></table></body></html>';

    const blob = new Blob([tableHTML], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${exportFileName}_${new Date().toISOString().slice(0,10)}.xls`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url); 
    setExportDialogOpen(false);
  };

  const handleExportPDF = () => {
    setExportMenuAnchor(null);
    
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Vă rugăm să permiteți pop-up-uri pentru a genera PDF-ul');
      return;
    }
    
    const filterInfo = [];
    if (selectedClass !== 'all') filterInfo.push(`Clasa: ${selectedClass}`);
    if (searchTerm) filterInfo.push(`Căutare: ${searchTerm}`);
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Centralizare Credite - ${new Date().toLocaleDateString('ro-RO')}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            padding: 20px;
            font-size: 11px;
          }
          .header { 
            text-align: center; 
            margin-bottom: 20px;
            border-bottom: 2px solid #333;
            padding-bottom: 15px;
          }
          .header h1 { font-size: 18px; margin-bottom: 5px; }
          .header p { color: #666; font-size: 12px; }
          .stats { 
            display: flex; 
            justify-content: space-around; 
            margin-bottom: 20px;
            background: #f5f5f5;
            padding: 10px;
            border-radius: 5px;
          }
          .stat { text-align: center; }
          .stat-value { font-size: 20px; font-weight: bold; color: #1976d2; }
          .stat-label { font-size: 10px; color: #666; }
          .filters { margin-bottom: 15px; font-style: italic; color: #666; }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            font-size: 10px;
          }
          th, td { 
            border: 1px solid #ddd; 
            padding: 6px 4px; 
            text-align: left; 
          }
          th { 
            background: #1976d2; 
            color: white; 
            font-weight: bold;
            font-size: 9px;
          }
          tr:nth-child(even) { background: #f9f9f9; }
          tr:hover { background: #e3f2fd; }
          .number { text-align: center; }
          .footer { 
            margin-top: 20px; 
            text-align: center; 
            font-size: 10px; 
            color: #999;
            border-top: 1px solid #ddd;
            padding-top: 10px;
          }
          @media print {
            body { padding: 10px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Centralizare Credite Elevi</h1>
          <p>Generat la: ${new Date().toLocaleString('ro-RO')}</p>
        </div>
        
        <div class="stats">
          <div class="stat">
            <div class="stat-value">${filteredStudents.length}</div>
            <div class="stat-label">Elevi</div>
          </div>
          <div class="stat">
            <div class="stat-value">${filteredStudents.reduce((sum, s) => sum + s.totalCredits, 0)}</div>
            <div class="stat-label">Credite Total</div>
          </div>
          <div class="stat">
            <div class="stat-value">${filteredStudents.length > 0 ? (filteredStudents.reduce((sum, s) => sum + s.totalCredits, 0) / filteredStudents.length).toFixed(1) : 0}</div>
            <div class="stat-label">Media/Elev</div>
          </div>
          <div class="stat">
            <div class="stat-value">${filteredStudents.reduce((sum, s) => sum + s.pendingRequests, 0)}</div>
            <div class="stat-label">În Așteptare</div>
          </div>
        </div>
        
        ${filterInfo.length > 0 ? `<div class="filters">Filtre aplicate: ${filterInfo.join(', ')}</div>` : ''}
        
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Nume</th>
              <th>Prenume</th>
              <th>Clasă</th>
              <th>Specializare</th>
              <th class="number">Curricular</th>
              <th class="number">Extracurric.</th>
              <th class="number">Transfer.</th>
              <th class="number">TOTAL</th>
              <th class="number">În Aștept.</th>
            </tr>
          </thead>
          <tbody>
            ${filteredStudents.map((student, index) => `
              <tr>
                <td>${index + 1}</td>
                <td>${student.nume}</td>
                <td>${student.prenume}</td>
                <td>${student.clasa || '-'}</td>
                <td>${getSpecializationName(student.specializare)}</td>
                <td class="number">${student.crediteCurriculare || 0}</td>
                <td class="number">${student.crediteExtracurriculare || 0}</td>
                <td class="number">${student.crediteTransferabile || 0}</td>
                <td class="number"><strong>${student.totalCredits}</strong></td>
                <td class="number">${student.pendingRequests}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="footer">
          SRB Credite - Sistem de Gestiune Credite Școlare
        </div>
        
        <script>
          window.onload = function() {
            window.print();
          }
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleViewStudentDetails = (studentId) => {
    navigate(`/student/${studentId}`);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
            Centralizare Credite
          </Typography>
          {selectedYear && (
            <SchoolYearSelect value={selectedYear} options={yearOptions} onChange={setSelectedYear} sx={{ mb: 1, minWidth: 160 }} />
          )}
        </Box>
        <Typography variant="body1" color="text.secondary">
          Vizualizare generală a tuturor elevilor și a progresului lor
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, width: '100%', mb: 4 }}>
        <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' } }}>
          <Card sx={{ height: '100%', minHeight: 120, bgcolor: 'primary.light', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ height: '100%' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                    {stats.totalStudents}
                  </Typography>
                  <Typography variant="body2">Elevi Total</Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' } }}>
          <Card sx={{ height: '100%', minHeight: 120, bgcolor: 'success.light', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ height: '100%' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                    {stats.averageCredits}
                  </Typography>
                  <Typography variant="body2">Credite/Medie</Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' } }}>
          <Card sx={{ height: '100%', minHeight: 120, bgcolor: 'warning.light', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ height: '100%' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                    {stats.pendingRequests}
                  </Typography>
                  <Typography variant="body2">Cereri În Așteptare</Typography>
                </Box>
                <PendingIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' } }}>
          <Card sx={{ height: '100%', minHeight: 120, bgcolor: 'info.light', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ height: '100%' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                    {stats.completedModules}
                  </Typography>
                  <Typography variant="body2">Module Finalizate</Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, width: '100%', alignItems: 'center' }}>
            <Box sx={{ flex: { xs: '0 0 100%', sm: '0 0 calc(50% - 12px)', md: '0 0 calc(33.333% - 16px)' } }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Caută elev..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ flex: { xs: '0 0 100%', sm: '0 0 calc(50% - 12px)', md: '0 0 calc(25% - 18px)' } }}>
              <FormControl fullWidth>
                <InputLabel>Filtrează după clasă</InputLabel>
                <Select
                  value={selectedClass}
                  label="Filtrează după clasă"
                  onChange={(e) => setSelectedClass(e.target.value)}
                >
                  <MenuItem value="all">Toate clasele</MenuItem>
                  {classes.map((cls) => (
                    <MenuItem key={cls} value={cls}>
                      Clasa {cls}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flex: { xs: '0 0 100%', sm: '0 0 calc(50% - 12px)', md: '0 0 calc(16.667% - 20px)' } }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                sx={{ height: '56px' }}
              >
                Export
              </Button>
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={() => setExportMenuAnchor(null)}
              >
                <MenuItem onClick={handleExportCSV}>
                  <ExcelIcon sx={{ mr: 1 }} /> Export CSV (Excel)
                </MenuItem>
                <MenuItem onClick={handleExportPDF}>
                  <PrintIcon sx={{ mr: 1 }} /> Export PDF / Print
                </MenuItem>
              </Menu>
            </Box>
          </Box>

          {/* Active filters display */}
          {(selectedClass !== 'all' || searchTerm) && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Filtre active:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {selectedClass !== 'all' && (
                  <Chip
                    label={`Clasa: ${selectedClass}`}
                    onDelete={() => setSelectedClass('all')}
                    color="primary"
                    size="small"
                  />
                )}
                {searchTerm && (
                  <Chip
                    label={`Căutare: "${searchTerm}"`}
                    onDelete={() => setSearchTerm('')}
                    color="info"
                    size="small"
                  />
                )}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* View Mode Toggle & Results Summary */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6">
          Elevi afișați: <strong>{filteredStudents.length}</strong> din {stats.totalStudents}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {filteredStudents.length !== stats.totalStudents && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setSelectedClass('all');
                setSearchTerm('');
              }}
            >
              Resetează filtrele
            </Button>
          )}
          <Tabs
            value={viewMode}
            onChange={(e, newValue) => setViewMode(newValue)}
            sx={{ minHeight: 40 }}
          >
            <Tab 
              icon={<ExcelIcon />} 
              label="Tabel" 
              value="table" 
              sx={{ minHeight: 40, py: 0 }}
            />
            <Tab 
              icon={<BarChartIcon />} 
              label="Grafice" 
              value="charts" 
              sx={{ minHeight: 40, py: 0 }}
            />
          </Tabs>
        </Box>
      </Box>

      {/* Charts View */}
      {viewMode === 'charts' && filteredStudents.length > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            {/* Chart Selector */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 250 }, width: { xs: '100%', sm: 'auto' } }}>
                <InputLabel>Selectează Graficul</InputLabel>
                <Select
                  value={selectedChart}
                  label="Selectează Graficul"
                  onChange={(e) => setSelectedChart(e.target.value)}
                >
                  {chartOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {opt.icon}
                        {opt.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary">
                Bazat pe {filteredStudents.length} elevi filtrați
              </Typography>
            </Box>

            {/* Chart Display - Full Width */}
            <Box sx={{ height: { xs: 300, sm: 500 } }}>
              {/* Credite pe Clase */}
              {selectedChart === 'creditsByClass' && (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData.creditsByClass} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} />
                    <YAxis />
                    <RechartsTooltip 
                      formatter={(value, name) => [value, name === 'medie' ? 'Medie/elev' : name === 'credite' ? 'Total credite' : 'Elevi']}
                    />
                    <Legend wrapperStyle={{ paddingTop: 20 }} />
                    <Bar dataKey="credite" name="Total Credite" fill="#2196F3" />
                    <Bar dataKey="elevi" name="Nr. Elevi" fill="#4CAF50" />
                  </BarChart>
                </ResponsiveContainer>
              )}

              {/* Distribuție Categorii */}
              {selectedChart === 'categoryDistribution' && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  {chartData.categoryDistribution.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={chartData.categoryDistribution}
                          cx="50%"
                          cy="50%"
                          innerRadius={80}
                          outerRadius={150}
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                        >
                          {chartData.categoryDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip formatter={(value) => [`${value} credite`, 'Total']} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography color="text.secondary">Nu există date pentru acest grafic</Typography>
                  )}
                </Box>
              )}

              {/* Top 10 Elevi */}
              {selectedChart === 'topStudents' && (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData.topStudents} layout="vertical" margin={{ top: 5, right: 30, left: 100, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={100} />
                    <RechartsTooltip 
                      formatter={(value, name, props) => [`${value} credite`, `Clasa: ${props.payload.clasa}`]}
                    />
                    <Legend />
                    <Bar dataKey="credite" name="Credite" fill="#FF9800" />
                  </BarChart>
                </ResponsiveContainer>
              )}

              {/* Distribuție Credite */}
              {selectedChart === 'creditDistribution' && (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData.creditDistribution} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip formatter={(value) => [`${value} elevi`, 'Nr. elevi']} />
                    <Legend />
                    <Bar dataKey="elevi" name="Număr Elevi" fill="#9C27B0" />
                  </BarChart>
                </ResponsiveContainer>
              )}

              {/* Categorii per Clasă - Stacked */}
              {selectedChart === 'stackedCategories' && (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData.creditsByClass} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend wrapperStyle={{ paddingTop: 20 }} />
                    <Bar dataKey="curricular" name="Curriculare" stackId="a" fill={CATEGORY_COLORS.curricular} />
                    <Bar dataKey="extracurricular" name="Extracurriculare" stackId="a" fill={CATEGORY_COLORS.extracurricular} />
                    <Bar dataKey="transferabil" name="Transferabile" stackId="a" fill={CATEGORY_COLORS.transferabil} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Students Table */}
      {viewMode === 'table' && filteredStudents.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <SchoolIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Nu s-au găsit elevi
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm || selectedClass !== 'all'
                ? 'Încearcă să modifici criteriile de căutare'
                : 'Încă nu există elevi în sistem'}
            </Typography>
          </CardContent>
        </Card>
      ) : viewMode === 'table' ? (
        <TableContainer component={Paper} sx={{ mb: 4, overflowX: 'auto' }}>
          <Table sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell><strong>Elev</strong></TableCell>
                <TableCell><strong>Clasă</strong></TableCell>
                <TableCell align="center">
                  <Tooltip title="Credite Curriculare">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <CurricularIcon fontSize="small" color="primary" />
                      <strong>Curr.</strong>
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Credite Extracurriculare">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <ExtracurricularIcon fontSize="small" color="warning" />
                      <strong>Extra.</strong>
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Credite Transferabile">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <TransferIcon fontSize="small" color="success" />
                      <strong>Trans.</strong>
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell align="center"><strong>Total</strong></TableCell>
                <TableCell><strong>Cereri</strong></TableCell>
                <TableCell><strong>Acțiuni</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredStudents.map((student) => {
                return (
                  <TableRow key={student.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">
                          {student.nume} {student.prenume}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {student.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`Clasa ${student.clasa || '-'}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={<CurricularIcon />}
                        label={student.crediteCurriculare || 0}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={<ExtracurricularIcon />}
                        label={student.crediteExtracurriculare || 0}
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={<TransferIcon />}
                        label={student.crediteTransferabile || 0}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="h6" color="primary" fontWeight="bold">
                        {student.totalCredits}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {student.pendingRequests > 0 && (
                          <Tooltip title={`${student.pendingRequests} în așteptare`}>
                            <Chip
                              icon={<PendingIcon />}
                              label={student.pendingRequests}
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                        {student.validatedRequests > 0 && (
                          <Tooltip title={`${student.validatedRequests} validate`}>
                            <Chip
                              icon={<CheckCircleIcon />}
                              label={student.validatedRequests}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                        {student.pendingRequests === 0 && student.validatedRequests === 0 && (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Vezi detalii">
                        <IconButton
                          color="primary"
                          onClick={() => handleViewStudentDetails(student.id)}
                          size="small"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}

      {/* Footer Stats */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, width: '100%', mt: 2 }}>
        <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(50% - 12px)' }, display: 'flex' }}>
          <Card variant="outlined" sx={{ height: '100%', width: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Distribuție credite
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                {filteredStudents.length > 0 ? (
                  <>
                    <Typography variant="h4" color="primary">
                      {Math.round(filteredStudents.reduce((sum, s) => sum + s.totalCredits, 0) / filteredStudents.length * 10) / 10}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      credite medii pe elev
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Nu există date
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(50% - 12px)' }, display: 'flex' }}>
          <Card variant="outlined" sx={{ height: '100%', width: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Elevi activi
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                {filteredStudents.length > 0 ? (
                  <>
                    <Typography variant="h4" color="success.main">
                      {filteredStudents.filter(s => s.totalCredits > 0).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      elevi cu credite acumulate
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Nu există date
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Dialog pentru opțiuni de export Excel */}
      <Dialog 
        open={exportDialogOpen} 
        onClose={() => setExportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ExcelIcon color="success" />
            Opțiuni Export Excel
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Selectați cum doriți să exportați datele elevilor:
          </Typography>
          
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={exportType}
              onChange={(e) => {
                setExportType(e.target.value);
                setExportClassFilter('all');
                setExportSpecFilter('all');
              }}
            >
              <FormControlLabel 
                value="all" 
                control={<Radio />} 
                label={
                  <Box>
                    <Typography fontWeight="medium">Toți elevii</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Exportă toți elevii din lista curentă ({filteredStudents.length} elevi)
                    </Typography>
                  </Box>
                }
              />
              
              <FormControlLabel 
                value="byClass" 
                control={<Radio />} 
                label={
                  <Box>
                    <Typography fontWeight="medium">Pe Clasă</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Selectați o anumită clasă pentru export
                    </Typography>
                  </Box>
                }
              />
              
              {exportType === 'byClass' && (
                <Box sx={{ ml: 4, mt: 1, mb: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Selectați clasa</InputLabel>
                    <Select
                      value={exportClassFilter}
                      label="Selectați clasa"
                      onChange={(e) => setExportClassFilter(e.target.value)}
                    >
                      <MenuItem value="all">— Selectați clasa —</MenuItem>
                      {classes.map((cls) => (
                        <MenuItem key={cls} value={cls}>
                          Clasa {cls} ({filteredStudents.filter(s => s.clasa === cls).length} elevi)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              
              <FormControlLabel 
                value="bySpecialization" 
                control={<Radio />} 
                label={
                  <Box>
                    <Typography fontWeight="medium">Pe Specializare</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Selectați o anumită specializare pentru export
                    </Typography>
                  </Box>
                }
              />
              
              {exportType === 'bySpecialization' && (
                <Box sx={{ ml: 4, mt: 1, mb: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Selectați specializarea</InputLabel>
                    <Select
                      value={exportSpecFilter}
                      label="Selectați specializarea"
                      onChange={(e) => setExportSpecFilter(e.target.value)}
                    >
                      <MenuItem value="all">— Selectați specializarea —</MenuItem>
                      {SPECIALIZATIONS.map((spec) => (
                        <MenuItem key={spec.code} value={spec.code}>
                          {spec.name} ({filteredStudents.filter(s => s.specializare === spec.code).length} elevi)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
            </RadioGroup>
          </FormControl>

          {/* Preview count */}
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Vor fi exportați: {' '}
              <strong>
                {exportType === 'all' 
                  ? filteredStudents.length 
                  : exportType === 'byClass' && exportClassFilter !== 'all'
                    ? filteredStudents.filter(s => s.clasa === exportClassFilter).length
                    : exportType === 'bySpecialization' && exportSpecFilter !== 'all'
                      ? filteredStudents.filter(s => s.specializare === exportSpecFilter).length
                      : filteredStudents.length
                } elevi
              </strong>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setExportDialogOpen(false)} color="inherit">
            Anulează
          </Button>
          <Button 
            onClick={performExport} 
            variant="contained" 
            color="success"
            startIcon={<DownloadIcon />}
            disabled={
              (exportType === 'byClass' && exportClassFilter === 'all') ||
              (exportType === 'bySpecialization' && exportSpecFilter === 'all')
            }
          >
            Exportă
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}